import {
  COLLECT_METRICS,
  METRIC_COLLECTION_INTERVAL,
} from '../utils/constants';
import { pushStats } from '../actions/PushStats';

class ProducerStats {
  constructor(producer, kind, token, socket) {
    this.producer = producer;
    this.kind = kind;
    this.token = token; // needed to post stats
    this.stats = {};
    this.layerMapping = {};
    this.interval = null;
    this.socket = socket;
    this.collect = COLLECT_METRICS; // Flag to enable/disable stats collection
  }

  destroy() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    this.producer = null;
    this.kind = null;
    this.token = null;
    this.stats = null;
    this.layerMapping = null;
  }

  async start() {
    if (!this.collect) return;
    if (this.interval) return;

    this.interval = setInterval(async () => {
      await this.getStats();
      await this.push();
    }, METRIC_COLLECTION_INTERVAL);
  }

  async stop() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    this.stats = {};
    this.layerMapping = {};
  }

  async getStats() {
    if (!this.producer || this.producer.closed) return;

    let serverStats;
    try {
      serverStats = await this.socket.request('producerStats', {
        id: this.producer.id,
      });
    } catch {
      serverStats = null;
    }

    if (!serverStats || !serverStats.hasOwnProperty('stats')) return;

    const activeSSRCs = new Set();

    serverStats.stats.forEach((stat) => {
      const layerId = stat.rid || stat.kind;
      this.stats[stat.ssrc] = {};
      this.stats[stat.ssrc].bitrate = stat.bitrate;
      this.stats[stat.ssrc].score = stat.score;
      this.stats[stat.ssrc].roundTripTime = stat.roundTripTime;
      this.stats[stat.ssrc].jitter = stat.jitter;
      this.stats[stat.ssrc].fractionLost = stat.fractionLost;
      this.layerMapping[layerId] = stat.ssrc;
      activeSSRCs.add(stat.ssrc); // Mark SSRC as active
    });

    if (this.kind === 'video') {
      const stats = Array.from((await this.producer.getStats()).values());
      stats.forEach((stat) => {
        if (stat.type === 'outbound-rtp' && stat.kind === this.kind) {
          if (this.stats[stat.ssrc]) {
            // if the layer is not in the server stats it is not being produced
            this.stats[stat.ssrc].fps = stat.framesPerSecond;
          }
        }
      });
    }

    // Cleanup: Remove SSRCs that were not found in this cycle or if there are no stats
    Object.keys(this.stats).forEach((ssrc) => {
      if (
        !activeSSRCs.has(parseInt(ssrc)) ||
        Object.keys(this.stats[ssrc]).length === 0
      ) {
        delete this.stats[ssrc];
      }
    });
  }

  async push() {
    // Only push if there are stats
    if (Object.entries(this.stats).length === 0) return;

    await pushStats(
      'producer-stats',
      {
        id: this.producer.id,
        kind: this.kind,
        trackId: this.producer.track.id,
        layers: this.layerMapping, // 'r1', 'r2', etc. mapped to SSRC
        stats: this.stats,
      },
      this.token
    );
  }
}

export default ProducerStats;
